import type { LineChartOption } from '@devstool/shadcn-echarts'
import type { MetricType } from './types'

import { format } from 'date-fns'
import * as echarts from 'echarts'

import { MetricDataType } from '@/entities/optimization-statistic'

import { LineChartComponent } from '@/shared/components/line-chart'
import { shadcnHtmlTooltipFormatter } from '@/shared/lib/echarts/chart-utils'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

const getColorsForIndex = (index: number) => {
	if (index === 0) {
		return {
			main: {
				itemStyle: { color: '#32B2F3' },
				lineStyle: { width: 2, color: '#32B2F3' },
				areaStyle: {
					opacity: 0.7,
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: 'rgba(50, 178, 243, 0.72)',
						},
						{
							offset: 1,
							color: 'rgba(50, 178, 243, 0.1)',
						},
					]),
				},
			},
			forecast: {
				itemStyle: { color: '#32B2F3' },
				lineStyle: {
					width: 2,
					type: 'dashed' as const,
					color: '#32B2F3',
				},
				areaStyle: {
					opacity: 0.7,
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: 'rgba(50, 178, 243, 0.70)',
						},
						{
							offset: 1,
							color: 'rgba(50, 178, 243, 0)',
						},
					]),
				},
			},
		}
	} else {
		return {
			main: {
				itemStyle: { color: '#8F98FF' },
				lineStyle: { width: 2, color: '#8F98FF' },
				areaStyle: {
					opacity: 0.7,
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: 'rgba(143, 152, 255, 0.72)',
						},
						{
							offset: 1,
							color: 'rgba(143, 152, 255, 0.1)',
						},
					]),
				},
			},
			forecast: {
				itemStyle: { color: '#8F98FF' },
				lineStyle: {
					width: 2,
					type: 'dashed' as const,
					color: '#8F98FF',
				},
				areaStyle: {
					opacity: 0.7,
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: 'rgba(143, 152, 255, 0.70)',
						},
						{
							offset: 1,
							color: 'rgba(143, 152, 255, 0.1)',
						},
					]),
				},
			},
		}
	}
}
export const getChartOption = (metrics: MetricType[]): LineChartOption => {
	const dataType = metrics[0].metricValueTypes.dataType
	const currency = metrics[0].currency
	const toPoint = (point: string[] | number[], visible: boolean) => [
		point[0],
		visible ? Number(point[1]) : null,
	]
	const series = metrics
		.map((metric, index) => {
			const length = metric.data.length
			const transitionIndex = length - 2
			const lastIndex = length - 1

			const { main, forecast } = getColorsForIndex(index)

			const common = {
				name: metric.name + ` (${index + 1})`,
				type: 'line' as const,
				showSymbol: true,
				symbol: 'circle',
				symbolSize: (_value: unknown, params: { dataIndex: number }) =>
					params.dataIndex === 0 ||
					params.dataIndex === transitionIndex ||
					params.dataIndex === lastIndex
						? 8
						: 0,
			}

			return [
				{
					...common,
					...main,
					smooth: true,
					data: metric.data.map((point, i) =>
						toPoint(point, i <= transitionIndex)
					),
				},
				{
					...common,
					...forecast,
					smooth: true,
					data: metric.data.map((point, i) =>
						toPoint(point, i >= transitionIndex)
					),
				},
			]
		})
		.flat()

	return {
		grid: { left: 8, right: 8, top: 16, bottom: 24, containLabel: true },
		tooltip: {
			axisPointer: { type: 'shadow' },
			trigger: 'axis',
			position: 'top',
			padding: 0,
			borderWidth: 0,
			backgroundColor: 'transparent',
			extraCssText:
				'padding:0;border:0;background:transparent;box-shadow:none;border-radius:0;',
			formatter: (params) =>
				shadcnHtmlTooltipFormatter(
					params,
					currency
						? (v) => formatMoney(v, currency, NumberFormats.Standart)
						: undefined
				),
		},
		xAxis: {
			type: 'time',
			splitNumber: 4,
			axisLabel: {
				fontSize: 10,
				hideOverlap: true,
				overflow: 'break',
				formatter(value) {
					return format(new Date(value), 'dd MMM')
				},
			},
			axisLine: { show: false },
			axisTick: { show: true },
		},
		yAxis: {
			type: 'value',
			axisLabel: {
				fontSize: 10,
				formatter: (value) => {
					if (dataType === MetricDataType.Percent) {
						return formatNumber(value, NumberFormats.Percent)
					}

					return currency
						? formatMoney(value, currency)
						: formatNumber(value, NumberFormats.Compact)
				},
			},
		},
		series,
	}
}
export default function MetricsPredictionChart({
	metrics,
}: {
	metrics: MetricType[]
}) {
	return <LineChartComponent height={250} option={getChartOption(metrics)} />
}
